import { Eyebrow } from "@/components/ui/eyebrow"
import { cn } from "@/lib/utils"

interface ApolloPanelProps {
  /** Uppercase micro-label at the top of the panel header. */
  eyebrow?: React.ReactNode
  /** Panel title under the eyebrow. */
  title?: React.ReactNode
  /** Right-aligned header actions. */
  actions?: React.ReactNode
  /** Extra classes merged onto the outer <section>. */
  className?: string
  /** Overrides the body padding — for dense panels like the report charts. */
  bodyClassName?: string
  /** Overrides the header padding, to match a tightened body. */
  headerClassName?: string
  /**
   * Extra classes on the actions wrapper. Default `shrink-0` keeps actions
   * on the eyebrow's line; a panel in a narrow column passes
   * `min-w-0 shrink grow` with a wrapping header so its actions drop under
   * the eyebrow and wrap, instead of running off the edge (ArtDent 261015).
   */
  actionsClassName?: string
  children: React.ReactNode
}

/**
 * Apollo content panel — a bordered surface card with an optional
 * eyebrow/title/actions header and a padded body. The header only renders when
 * at least one of eyebrow/title/actions is supplied, so the panel doubles as a
 * plain padded surface. Shared by the migrated detail/dashboard pages.
 */
export function ApolloPanel({
  eyebrow,
  title,
  actions,
  className,
  bodyClassName,
  headerClassName,
  actionsClassName,
  children,
}: ApolloPanelProps) {
  const hasHeader = eyebrow != null || title != null || actions != null

  return (
    <section className={cn("rounded-lg border border-border bg-surface", className)}>
      {hasHeader && (
        <div className={cn("flex items-baseline justify-between gap-3 px-4 pt-4 md:px-5", headerClassName)}>
          <div>
            {eyebrow != null && <Eyebrow className="block">{eyebrow}</Eyebrow>}
            {title != null && (
              <div className="mt-1 text-[15px] font-medium">{title}</div>
            )}
          </div>
          {actions != null && (
            <div className={cn("flex shrink-0 items-center gap-2", actionsClassName)}>{actions}</div>
          )}
        </div>
      )}
      <div className={cn("p-4 md:p-5", bodyClassName)}>{children}</div>
    </section>
  )
}
